import React, { useState, useCallback, useMemo } from 'react';
import { useApiConfig } from '../context/ApiConfigContext';

// Highlight :param segments in a path string
function PathWithParams({ path }) {
  const parts = path.split(/(:[\w]+)/g);
  return (
    <span className="endpoint-path">
      {parts.map((part, i) =>
        part.startsWith(':')
          ? <span key={i} className="path-param">{part}</span>
          : part
      )}
    </span>
  );
}

// Syntax-highlight a JSON string
function highlightJSON(text) {
  if (!text) return '';
  let escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  return escaped
    .replace(/(\/\*[^*]*\*+(?:[^/*][^*]*\*+)*\/)/g, '<span class="json-comment">$1</span>')
    .replace(/"([^"]+)"(\s*:)/g, '<span class="json-key">"$1"</span>$2')
    .replace(/:\s*"([^"]*)"/g, (match, val) => `: <span class="json-str">"${val}"</span>`)
    .replace(/:\s*(-?\d+\.?\d*)/g, ': <span class="json-num">$1</span>')
    .replace(/:\s*(true|false)/g, ': <span class="json-bool">$1</span>')
    .replace(/:\s*(null)/g, ': <span class="json-null">$1</span>');
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [text]);

  return (
    <button className="copy-btn" onClick={handleCopy}>
      {copied ? 'copied!' : 'copy'}
    </button>
  );
}

function ParamTable({ data, isError }) {
  if (!data || !data.rows || data.rows.length === 0) return null;

  if (isError) {
    return (
      <table className="param-table">
        <thead>
          <tr>
            <th>Code</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row, i) => (
            <tr key={i}>
              <td><span className="error-code">{row[0]}</span></td>
              <td style={{ color: 'var(--muted)', fontSize: '12px' }}>{row[1]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <table className="param-table">
      <thead>
        <tr>
          {data.headers.map((h, i) => <th key={i}>{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {data.rows.map((row, i) => (
          <tr key={i}>
            {row.map((cell, j) => {
              if (j === 0) return <td key={j}><span className="param-name">{cell}</span></td>;
              if (j === 1) return <td key={j}><span className="param-type">{cell}</span></td>;
              if (j === 2) {
                const isYes = /^yes$/i.test(cell);
                return (
                  <td key={j}>
                    {isYes
                      ? <span className="req-yes">Yes</span>
                      : <span className="req-no">{cell || 'No'}</span>
                    }
                  </td>
                );
              }
              return <td key={j} style={{ color: 'var(--muted)', fontSize: '12px' }}>{cell}</td>;
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Build a default JSON body object from the requestBody table
function buildDefaultBody(requestBody) {
  if (!requestBody || !requestBody.rows) return {};
  const obj = {};
  for (const row of requestBody.rows) {
    const name = row[0];
    const type = (row[1] || '').toLowerCase();
    if (!name || name.startsWith('-') || name.startsWith('|')) continue;
    if (type.includes('bool')) obj[name] = false;
    else if (type.includes('int') || type.includes('number') || type.includes('float')) obj[name] = 0;
    else if (type.includes('array') || type.includes('[]')) obj[name] = [];
    else if (type.includes('object')) obj[name] = {};
    else obj[name] = '';
  }
  return obj;
}

// Extract :paramName segments from a path
function extractPathParamNames(path) {
  const matches = path.match(/:[\w]+/g) || [];
  return matches.map(m => m.slice(1));
}

// ─── Test Now Section ────────────────────────────────────────────────────────
function TestSection({ endpoint }) {
  const { baseUrl, token } = useApiConfig();
  const { method, path, queryParams, requestBody } = endpoint;

  const pathParamNames = useMemo(() => extractPathParamNames(path), [path]);
  const hasBody = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);

  const [pathParamVals, setPathParamVals] = useState(() =>
    Object.fromEntries(pathParamNames.map(n => [n, '']))
  );

  const [queryParamVals, setQueryParamVals] = useState(() => {
    if (!queryParams) return {};
    return Object.fromEntries(queryParams.rows.map(r => [r[0], '']));
  });

  const [bodyText, setBodyText] = useState(() => {
    if (!hasBody) return '';
    if (!requestBody) return '{\n  \n}';
    return JSON.stringify(buildDefaultBody(requestBody), null, 2);
  });

  const [isSending, setIsSending] = useState(false);
  const [response, setResponse] = useState(null);

  // Build the full URL live as inputs change
  const fullUrl = useMemo(() => {
    if (!baseUrl) return '';
    let p = path;
    for (const [name, val] of Object.entries(pathParamVals)) {
      p = p.replace(`:${name}`, val ? encodeURIComponent(val) : `:${name}`);
    }
    const qp = new URLSearchParams();
    if (token) qp.set('token', token);
    for (const [key, val] of Object.entries(queryParamVals)) {
      if (val !== '') qp.set(key, val);
    }
    const qs = qp.toString();
    return `${baseUrl}${p}${qs ? '?' + qs : ''}`;
  }, [baseUrl, token, path, pathParamVals, queryParamVals]);

  const handleSend = async () => {
    if (!baseUrl) return;
    setIsSending(true);
    setResponse(null);

    const opts = { method, headers: {} };

    if (hasBody && bodyText.trim()) {
      try {
        JSON.parse(bodyText);
        opts.body = bodyText;
        opts.headers['Content-Type'] = 'application/json';
      } catch (e) {
        setResponse({ error: `Invalid JSON in request body: ${e.message}` });
        setIsSending(false);
        return;
      }
    }

    try {
      const res = await fetch(fullUrl, opts);
      const text = await res.text();
      let body;
      try { body = JSON.parse(text); }
      catch { body = text; }
      setResponse({ status: res.status, statusText: res.statusText, ok: res.ok, body });
    } catch (err) {
      setResponse({ error: `Network error: ${err.message}` });
    } finally {
      setIsSending(false);
    }
  };

  // Not configured yet — show a prompt
  if (!baseUrl) {
    return (
      <div className="test-no-config">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
        </svg>
        Click the user icon in the top bar to configure your Base URL and Token
      </div>
    );
  }

  const bodyLines = bodyText.split('\n').length;

  return (
    <div className="test-section">
      {/* Live URL preview */}
      <div className="test-url-bar">
        <span className={`method-tag tag-${method.toLowerCase()} test-method-tag`}>{method}</span>
        <span className="test-url-text">{fullUrl || `${baseUrl}${path}`}</span>
        <CopyButton text={fullUrl || `${baseUrl}${path}`} />
      </div>

      <div className="test-panels">
        {/* ── Left: inputs ── */}
        <div className="test-left">
          {/* Path params */}
          {pathParamNames.length > 0 && (
            <div className="test-group">
              <div className="test-group-label">Path Parameters</div>
              {pathParamNames.map(name => (
                <div key={name} className="test-param-row">
                  <span className="test-param-name">:{name}</span>
                  <input
                    className="test-input"
                    placeholder={name}
                    value={pathParamVals[name] || ''}
                    onChange={e => setPathParamVals(v => ({ ...v, [name]: e.target.value }))}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Query params */}
          {queryParams && queryParams.rows.length > 0 && (
            <div className="test-group">
              <div className="test-group-label">Query Parameters</div>
              {queryParams.rows.map((row, i) => (
                <div key={i} className="test-param-row">
                  <span className="test-param-name">{row[0]}</span>
                  <input
                    className="test-input"
                    placeholder={row[3] || row[2] || row[1] || ''}
                    value={queryParamVals[row[0]] || ''}
                    onChange={e => setQueryParamVals(v => ({ ...v, [row[0]]: e.target.value }))}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Token note */}
          <div className="test-token-note">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            Token added automatically as <code>?token=…</code>
          </div>

          {/* Request body */}
          {hasBody && (
            <div className="test-group">
              <div className="test-group-label">Request Body <span className="test-group-badge">JSON</span></div>
              <textarea
                className="test-body-editor"
                value={bodyText}
                onChange={e => setBodyText(e.target.value)}
                rows={Math.min(Math.max(bodyLines + 1, 6), 20)}
                spellCheck={false}
              />
            </div>
          )}

          <button
            className="test-send-btn"
            onClick={handleSend}
            disabled={isSending}
          >
            {isSending ? (
              <>
                <span className="test-spinner" />
                Sending…
              </>
            ) : (
              <>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="5,3 19,12 5,21" />
                </svg>
                Send Request
              </>
            )}
          </button>
        </div>

        {/* ── Right: response ── */}
        {response && (
          <div className="test-right">
            <div className="test-group-label">
              Response
              {!response.error && (
                <span className={`test-status-badge ${response.ok ? 'status-ok' : 'status-err'}`}>
                  {response.status} {response.statusText}
                </span>
              )}
            </div>

            {response.error ? (
              <div className="test-response-error">{response.error}</div>
            ) : (
              <div className="test-response-body">
                <CopyButton text={
                  typeof response.body === 'string'
                    ? response.body
                    : JSON.stringify(response.body, null, 2)
                } />
                <pre dangerouslySetInnerHTML={{
                  __html: typeof response.body === 'string'
                    ? response.body
                    : highlightJSON(JSON.stringify(response.body, null, 2))
                }} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main EndpointCard ───────────────────────────────────────────────────────
export default function EndpointCard({ endpoint }) {
  const [isOpen, setIsOpen] = useState(false);

  const { method, path, title, desc, auth, urlParams, queryParams, requestBody, response, errors } = endpoint;
  const methodLower = method.toLowerCase();

  const highlighted = isOpen ? highlightJSON(response) : '';

  return (
    <div className={`endpoint-card${isOpen ? ' open' : ''}`}>
      {/* Header (clickable) */}
      <div className="endpoint-header" onClick={() => setIsOpen(o => !o)}>
        <span className={`method-tag tag-${methodLower}`}>{method}</span>

        <div className="endpoint-meta">
          {title && <div className="endpoint-title">{title}</div>}
          <PathWithParams path={path} />
          {desc && !isOpen && <div className="endpoint-desc">{desc}</div>}
        </div>

        <div className="endpoint-right">
          <span className={`auth-badge ${auth ? 'auth-yes' : 'auth-no'}`}>
            {auth ? '🔒 Auth' : 'Public'}
          </span>
          <span className={`toggle-icon ${isOpen ? 'open' : ''}`}>▾</span>
        </div>
      </div>

      {/* Body (expanded) */}
      {isOpen && (
        <div className="endpoint-body">
          {urlParams && (
            <>
              <div className="section-label">URL Parameters</div>
              <ParamTable data={urlParams} />
            </>
          )}

          {queryParams && (
            <>
              <div className="section-label">Query Parameters</div>
              <ParamTable data={queryParams} />
            </>
          )}

          {requestBody && (
            <>
              <div className="section-label">Request Body</div>
              <ParamTable data={requestBody} />
            </>
          )}

          {response && (
            <>
              <div className="section-label">Response</div>
              <div className="code-block">
                <CopyButton text={response} />
                <pre dangerouslySetInnerHTML={{ __html: highlighted }} />
              </div>
            </>
          )}

          {errors && (
            <>
              <div className="section-label">Error Codes</div>
              <ParamTable data={errors} isError />
            </>
          )}

          {/* ── Test Now ── */}
          <div className="test-now-divider">
            <div className="test-now-label">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                <polygon points="5,3 19,12 5,21" />
              </svg>
              Test Now
            </div>
          </div>
          <TestSection endpoint={endpoint} />
        </div>
      )}
    </div>
  );
}
